import { useQuery } from "@tanstack/react-query";
import {
  getSummary,
  getDashboardOrders,
  getDashboardUsers,
  getDashboardProducts,
  getDashboardCategories,
  getDashboardOffers,
} from "../../../services/dashboard";

function useAdminDashboard() {
  const summaryQuery = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: getSummary,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const ordersQuery = useQuery({
    queryKey: ["dashboard", "orders"],
    queryFn: getDashboardOrders,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const usersQuery = useQuery({
    queryKey: ["dashboard", "users"],
    queryFn: getDashboardUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const productsQuery = useQuery({
    queryKey: ["dashboard", "products"],
    queryFn: getDashboardProducts,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const categoriesQuery = useQuery({
    queryKey: ["dashboard", "categories"],
    queryFn: getDashboardCategories,
    staleTime: 15 * 60 * 1000, // 15 minutes
  });

  const offersQuery = useQuery({
    queryKey: ["dashboard", "offers"],
    queryFn: getDashboardOffers,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    // Data
    summary: summaryQuery.data,
    orders: ordersQuery.data,
    users: usersQuery.data,
    products: productsQuery.data,
    categories: categoriesQuery.data,
    offers: offersQuery.data,

    // Loading states
    isLoading: {
      summary: summaryQuery.isLoading,
      orders: ordersQuery.isLoading,
      users: usersQuery.isLoading,
      products: productsQuery.isLoading,
      categories: categoriesQuery.isLoading,
      offers: offersQuery.isLoading,
    },

    // Error states
    errors: {
      summary: summaryQuery.error,
      orders: ordersQuery.error,
      users: usersQuery.error,
      products: productsQuery.error,
      categories: categoriesQuery.error,
      offers: offersQuery.error,
    },

    // Fetching states (for background refetches)
    isFetching: {
      summary: summaryQuery.isFetching,
      orders: ordersQuery.isFetching,
      users: usersQuery.isFetching,
      products: productsQuery.isFetching,
      categories: categoriesQuery.isFetching,
      offers: offersQuery.isFetching,
    },

    // Refetch functions
    refetch: {
      summary: summaryQuery.refetch,
      orders: ordersQuery.refetch,
      users: usersQuery.refetch,
      products: productsQuery.refetch,
      categories: categoriesQuery.refetch,
      offers: offersQuery.refetch,
      all: () => {
        summaryQuery.refetch();
        ordersQuery.refetch();
        usersQuery.refetch();
        productsQuery.refetch();
        categoriesQuery.refetch();
        offersQuery.refetch();
      },
    },

    // Overall loading state
    isAnyLoading:
      summaryQuery.isLoading ||
      ordersQuery.isLoading ||
      usersQuery.isLoading ||
      productsQuery.isLoading ||
      categoriesQuery.isLoading ||
      offersQuery.isLoading,

    // Check if any query has errors
    hasErrors:
      summaryQuery.isError ||
      ordersQuery.isError ||
      usersQuery.isError ||
      productsQuery.isError ||
      categoriesQuery.isError ||
      offersQuery.isError,
  };
}

export default useAdminDashboard;
